// Runs in the drawer, newest first, each with a Live or Test pill, its outcome, who started it
// and what it fetched per job. A failed run carries its triage inline (page class, where it
// stopped, its screenshots, Hand to dev for a fix…); a completed test run offers Promote.
// With the history API it covers 14 days (today open, earlier days behind a button); without
// it, today's plan entries.

import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { RUN_LABEL, byText, canPromote, fmtClock, fmtDay, jobName, runColour, runJobsText } from './deskModel';
import { TEST, Tag, useTones } from './tones';

const FAILED = ['failed', 'stopped'];

const Run = ({ row, run, shots, onShot, onFix, onPromote, canFix }) => {
    const t = useTones();
    const jobs = runJobsText(row, run);
    const what = run.job_types?.length && !jobs.length ? run.job_types.map((jt) => jobName(row, jt)).join(', ') : null;
    const files = run.screenshots?.length ? run.screenshots : shots || [];
    return (
        <Box component="li" data-run={run.key} sx={{
            display: 'grid', gridTemplateColumns: '68px 1fr', gap: 1, py: 1,
            borderTop: '1px solid var(--color-border, #e0e0e0)', listStyle: 'none',
            ...(run.scratch ? { borderLeft: `3px solid ${t.text(TEST)}`, pl: 1 } : {}),
        }}>
            <Typography className="d-font" sx={{ fontSize: 12, color: t.muted, pt: '2px' }}>{run.at ? fmtClock(run.at, row.timezone) : 'planned'}</Typography>
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    <Tag label={run.scratch ? 'Test' : 'Live'} colour={run.scratch ? TEST : '#1565c0'} />
                    <Tag label={RUN_LABEL[run.status] || run.status || '?'} colour={runColour(run.status)} />
                    <Typography component="span" className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                        {byText(run)}{what ? ` · ${what}` : ''}
                    </Typography>
                </Box>
                {jobs.map((l) => <Typography key={l} className="d-font" sx={{ fontSize: 12.5, mt: 0.25 }}>{l}</Typography>)}
                {run.message && <Typography className="d-font" sx={{ fontSize: 12.5, mt: 0.25, overflowWrap: 'anywhere' }}>{run.message}</Typography>}
                {FAILED.includes(run.status) && (run.page_class || run.stopped_at) && (
                    <Typography className="d-font" sx={{ fontSize: 12, mt: 0.25, color: t.bad }}>
                        {run.stopped_at ? <>Stopped at <code>{run.stopped_at}</code></> : null}
                        {run.stopped_at && run.page_class ? ' · ' : ''}
                        {run.page_class ? `page class: ${run.page_class}` : ''}
                    </Typography>
                )}
                {files.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                        {files.map((s) => (
                            <Button key={s.name || s.url} size="small" variant="outlined" onClick={() => onShot(s)}
                                sx={{ textTransform: 'none', fontSize: 11, py: 0 }}>
                                {String(s.name || s.url).split('/').pop()}
                            </Button>
                        ))}
                    </Box>
                )}
                {FAILED.includes(run.status) && !run.scratch && canFix && (
                    <Button size="small" sx={{ textTransform: 'none', mt: 0.25, px: 0 }} onClick={onFix}>Hand to dev for a fix…</Button>
                )}
                {canPromote(run) && (
                    <Button size="small" variant="outlined" sx={{ textTransform: 'none', mt: 0.75 }} onClick={() => onPromote(run)}>
                        Compare with live · Promote…
                    </Button>
                )}
            </Box>
        </Box>
    );
};

// runs: historyRuns(...) output. shotsByRun: run_id -> screenshot files.
const RunTimeline = ({ row, runs, shotsByRun, onShot, onFix, onPromote, canFix }) => {
    const t = useTones();
    const [earlier, setEarlier] = useState(false);
    const today = row.today?.day;
    const days = [...new Set(runs.map((r) => r.day || today))];
    const shown = earlier ? days : days.filter((d) => d === today);
    const hidden = runs.filter((r) => (r.day || today) !== today).length;
    if (!runs.length) return <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>No runs today.</Typography>;
    return (
        <Box>
            {!shown.length && <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>No runs today.</Typography>}
            {shown.map((d) => (
                <Box key={d} sx={{ mb: 1 }}>
                    {(earlier || d !== today) && (
                        <Typography className="d-font" sx={{ fontSize: 12, fontWeight: 700, mt: 1 }}>{d === today ? 'Today' : fmtDay(d)}</Typography>
                    )}
                    <Box component="ul" sx={{ m: 0, p: 0 }}>
                        {runs.filter((r) => (r.day || today) === d).slice().reverse().map((r) => (
                            <Run key={r.key} row={row} run={r} shots={r.run_id ? shotsByRun[r.run_id] : null}
                                onShot={onShot} onFix={onFix} onPromote={onPromote} canFix={canFix} />
                        ))}
                    </Box>
                </Box>
            ))}
            {hidden > 0 && (
                <Button size="small" sx={{ textTransform: 'none', px: 0 }} onClick={() => setEarlier((v) => !v)}>
                    {earlier ? 'Today only' : `Earlier days (${hidden} run${hidden === 1 ? '' : 's'})`}
                </Button>
            )}
        </Box>
    );
};

export default RunTimeline;
